/**
 * The header (1.I-1 deliverable 7): breadcrumbs, `document.title`, and an end region that
 * `ShellHeaderItems` portals into. On `/doc/$id` the crumbs are the home chain and the open
 * document's title; on another route its `staticData.crumb`, else nothing.
 */

import { IconButton } from '@codex/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMatches } from '@tanstack/react-router';
import { useEffect } from 'react';
import { docHref } from '../editor-services/adapter.js';
import { DocSlotOutlet } from '../editor-services/slots.js';
import { type Crumb, pageTitle, walkHomeChain } from './breadcrumbs.js';
import { HeaderPlaces } from './header-slot.js';
import { useOpenDoc } from './open-doc.js';
import { usePinActions } from './pins.js';
import { fetchMeta } from './queries.js';
import './static-data.js';

const DOC_ROUTE = '/doc/$id';

/** The open document's id when the route is the document page. */
export function useOpenDocId(): string | null {
  const matches = useMatches();
  const last = matches[matches.length - 1];
  if (!last || last.routeId !== DOC_ROUTE) return null;
  const id = (last.params as { id?: string }).id;
  return id ?? null;
}

function useRouteCrumb(): string | null {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const crumb = matches[i]?.staticData?.crumb;
    if (crumb) return crumb;
  }
  return null;
}

/** The open document's home chain, root first, ending with the document. */
export function useChain(docId: string | null) {
  const client = useQueryClient();
  return useQuery({
    queryKey: ['crumbs', docId],
    enabled: docId !== null,
    queryFn: () => walkHomeChain(docId as string, (id) => fetchMeta(client, id), { maxHops: 8 }),
  });
}

export function useCrumbs(): readonly Crumb[] {
  const docId = useOpenDocId();
  const routeCrumb = useRouteCrumb();
  const chain = useChain(docId);
  if (docId !== null) return chain.data ?? [];
  return routeCrumb ? [{ id: '', title: routeCrumb }] : [];
}

export function Breadcrumbs({ crumbs }: { crumbs: readonly Crumb[] }) {
  const open = useOpenDoc();
  return (
    <nav className="cx-breadcrumbs" aria-label="Breadcrumb" data-testid="shell.header.breadcrumbs">
      <ol>
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <li key={crumb.id || crumb.title} aria-current={last ? 'location' : undefined}>
              {last || crumb.id === '' ? (
                <span className="cx-shell-crumb">{crumb.title}</span>
              ) : (
                <a
                  className="cx-shell-crumb cx-shell-link"
                  href={docHref(crumb.id)}
                  onClick={(event) => open(crumb.id, event)}
                >
                  {crumb.title}
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** "Pin page": the keyboard route to pinning, on a document's page (pins it, or unpins it). */
export function PinPageToggle({ docId }: { docId: string }) {
  const actions = usePinActions();
  const itemId = actions.pinned.get(docId);
  return (
    <IconButton
      icon="pin"
      label="Pin page"
      pressed={itemId !== undefined}
      onClick={() => void (itemId === undefined ? actions.pin(docId) : actions.unpin(itemId))}
    />
  );
}

export function ShellHeader({ endRef }: { endRef: (element: HTMLElement | null) => void }) {
  const crumbs = useCrumbs();
  const docId = useOpenDocId();
  const lastTitle = crumbs.length > 0 ? (crumbs[crumbs.length - 1]?.title ?? null) : null;
  useEffect(() => {
    document.title = pageTitle(lastTitle);
  }, [lastTitle]);
  return (
    <header className="cx-header" data-testid="shell.header.root">
      <div className="cx-header__start">
        <Breadcrumbs crumbs={crumbs} />
      </div>
      {docId !== null && <DocSlotOutlet slot="header.modes" docId={docId} />}
      <div className="cx-header__end">
        {docId !== null && <PinPageToggle docId={docId} />}
        <div className="cx-header__items" ref={endRef}>
          <HeaderPlaces />
        </div>
      </div>
    </header>
  );
}
